/**
 * The push-permission status row — in the app shell (DashboardLayout), so it
 * appears on every authenticated page, above the routed content.
 *
 * Design authority: `docs/handoff-notifications-milestone.md` §M4. Explicitly
 * NOT a persistent full-width button: the bottom of the screen is already
 * `BottomTabBar`, and a passive always-visible button invites the idle tap
 * that permanently burns the one-shot permission grant (see
 * `push/pushState.ts`). Instead this is a status row that tells the truth
 * about all seven `PushState` values, and only ever offers an action when
 * `canRequestPermission` says the browser prompt is still available — which
 * today is exactly `off`.
 *
 * `blocked` and `needs_install` render no button on purpose: permission is
 * one-shot and there is no programmatic recovery from either (see
 * `pushState.ts`'s doc on `derivePushState`), so a button there would
 * silently no-op and teach the customer the app is broken.
 *
 * `lapsed` DOES get a button here: `canSelfHeal('lapsed')` is true, and
 * `usePushStatus.selfHeal()` is exactly the resubscribe it drives — no
 * prompt involved, since permission is already granted in that state. It
 * still gets the interrupting, high-attention (`role="alert"`) treatment its
 * "broken promise" framing calls for; the button is the fix, not a
 * downgrade of the urgency.
 */
import { useTranslation } from 'react-i18next';

import { canRequestPermission, canSelfHeal, isDeliverable, type PushState } from '../push/pushState.ts';
import { usePushStatus, type UsePushStatusOptions } from '../push/usePushStatus.ts';
import { IconAlert, IconBell, IconCheck } from './icons.tsx';
import styles from './PushStatusWidget.module.css';

type Severity = 'neutral' | 'success' | 'danger';

/** How urgently each state should read, visually. Pairs with an icon and copy — never colour alone. */
const SEVERITY: Record<PushState, Severity> = {
  needs_install: 'neutral',
  blocked: 'danger',
  unsupported: 'neutral',
  not_ready: 'neutral',
  on: 'success',
  lapsed: 'danger',
  // Not 'danger': nothing is broken and the customer did nothing wrong — the
  // phone simply belonged to another account before. One tap fixes it.
  foreign: 'neutral',
  off: 'neutral',
};

function stateIcon(state: PushState): React.JSX.Element {
  switch (state) {
    case 'on':
      return <IconCheck size={22} />;
    case 'blocked':
    case 'lapsed':
      return <IconAlert size={22} />;
    default:
      return <IconBell size={22} />;
  }
}

export interface PushStatusWidgetProps {
  /**
   * Test-only injection point, forwarded verbatim to `usePushStatus`.
   * Production mounts this with no props and hits the real browser APIs.
   */
  pushStatusOptions?: UsePushStatusOptions;

  /**
   * Whether to render the one state that reports GOOD news.
   *
   * This widget lives in the app shell, so it appears on every screen. The
   * states that mean something is wrong should follow the customer everywhere —
   * `blocked` and `lapsed` especially, since a customer who believes
   * notifications are working stops visiting the screen that would tell them
   * otherwise. But `on` means everything is fine, and a permanent "Avisos
   * ativados" banner on every page is noise that trains people to ignore the
   * row entirely — including the times it is telling them something real.
   *
   * So `on` renders only where someone would go looking to confirm it: the
   * notifications page. Defaults to false, because the shell is the common
   * case; the caller that wants confirmation opts in.
   */
  showWhenDeliverable?: boolean;
}

export function PushStatusWidget({
  pushStatusOptions,
  showWhenDeliverable = false,
}: PushStatusWidgetProps = {}): React.JSX.Element {
  const { t } = useTranslation();
  const { state, loading, requestPermission, selfHeal } = usePushStatus(pushStatusOptions);

  // Render nothing rather than flash a wrong state while the first
  // environment read is in flight — see `usePushStatus`'s own doc on why
  // `loading` starts true.
  if (loading) {
    return <></>;
  }

  // `isDeliverable` is true for `on` and nothing else, so the rule reads as
  // what it means: suppress the all-clear unless this caller asked for it.
  if (isDeliverable(state) && !showWhenDeliverable) {
    return <></>;
  }

  const severity = SEVERITY[state];
  // Exactly one of these is ever true for a given state — `off` can only
  // request, `lapsed` can only self-heal, everything else neither.
  const action = canRequestPermission(state) ? requestPermission : canSelfHeal(state) ? selfHeal : null;

  return (
    <div
      className={`${styles['widget']} ${styles[`severity-${severity}`]}`}
      role={severity === 'danger' ? 'alert' : 'status'}
      data-push-state={state}
    >
      <span className={`${styles['icon']} ns-icon-tile`} aria-hidden="true">
        {stateIcon(state)}
      </span>
      <div className={styles['text']}>
        <p className={styles['title']}>{t(`push.${state}.title`)}</p>
        <p className={styles['body']}>{t(`push.${state}.body`)}</p>
        {action && (
          <button type="button" className={`ns-cta ${styles['action']}`} onClick={() => void action()}>
            {t(`push.${state}.action`)}
          </button>
        )}
      </div>
    </div>
  );
}
